<template>
	<div class="webrtc-container">
		<!-- 浏览器兼容性检查 -->
		<div v-if="!isWebRTCSupported" class="browser-check">
			<div class="browser-header">
				<i class="fas fa-exclamation-triangle"></i>
				<h2>浏览器兼容性检测</h2>
			</div>

			<div class="browser-status">
				<div class="status-item" :class="{'status-pass': navigatorCheck, 'status-fail': !navigatorCheck}">
					<i class="fas" :class="navigatorCheck ? 'fa-check-circle' : 'fa-times-circle'"></i>
					<span>navigator对象: {{ navigatorCheck ? '支持' : '不支持' }}</span>
				</div>

				<div class="status-item" :class="{'status-pass': mediaDevicesCheck, 'status-fail': !mediaDevicesCheck}">
					<i class="fas" :class="mediaDevicesCheck ? 'fa-check-circle' : 'fa-times-circle'"></i>
					<span>navigator.mediaDevices: {{ mediaDevicesCheck ? '支持' : '不支持' }}</span>
				</div>

				<div class="status-item" :class="{'status-pass': getUserMediaCheck, 'status-fail': !getUserMediaCheck}">
					<i class="fas" :class="getUserMediaCheck ? 'fa-check-circle' : 'fa-times-circle'"></i>
					<span>getUserMedia API: {{ getUserMediaCheck ? '支持' : '不支持' }}</span>
				</div>

				<div class="status-item" :class="{'status-pass': peerConnectionCheck, 'status-fail': !peerConnectionCheck}">
					<i class="fas" :class="peerConnectionCheck ? 'fa-check-circle' : 'fa-times-circle'"></i>
					<span>RTCPeerConnection: {{ peerConnectionCheck ? '支持' : '不支持' }}</span>
				</div>

				<div class="status-item" :class="{'status-pass': isSecureContext, 'status-fail': !isSecureContext}">
					<i class="fas" :class="isSecureContext ? 'fa-check-circle' : 'fa-times-circle'"></i>
					<span>安全上下文(HTTPS): {{ isSecureContext ? '是' : '否' }}</span>
				</div>
			</div>

			<div class="browser-recommendation">
				<h3>推荐浏览器</h3>
				<div class="browser-list">
					<div class="browser-item">
						<i class="fab fa-chrome"></i>
						<span>Google Chrome</span>
						<span>最新版本</span>
					</div>
					<div class="browser-item">
						<i class="fab fa-firefox"></i>
						<span>Mozilla Firefox</span>
						<span>最新版本</span>
					</div>
					<div class="browser-item">
						<i class="fab fa-edge"></i>
						<span>Microsoft Edge</span>
						<span>最新版本</span>
					</div>
					<div class="browser-item">
						<i class="fab fa-safari"></i>
						<span>Apple Safari</span>
						<span>最新版本</span>
					</div>
				</div>
			</div>

			<div class="browser-troubleshooting">
				<h3>故障排除</h3>
				<ul>
					<li v-if="!isSecureContext">
						<i class="fas fa-lock"></i>
						请确保通过HTTPS访问本应用
					</li>
					<li>
						<i class="fas fa-plug"></i>
						检查浏览器插件是否阻止了媒体设备访问
					</li>
					<li>
						<i class="fas fa-camera"></i>
						确保您的设备已连接摄像头和麦克风
					</li>
					<li>
						<i class="fas fa-sync-alt"></i>
						尝试刷新页面或重启浏览器
					</li>
				</ul>
			</div>
		</div>

		<!-- 主应用内容 -->
		<div v-else>
			<div class="header">
				<h1><i class="fas fa-desktop"></i>视频辅导</h1>
				<p>学管师可通过实时视频互动与屏幕共享，观察学生刷题过程并提供个性化指导</p>
			</div>

			<!-- 房间管理区域 -->
			<div class="room-management" v-if="!isConnected">
				<div class="room-options">
					<label>
						<input type="radio" v-model="roomOption" value="create"> 创建新房间
					</label>
					<label>
						<input type="radio" v-model="roomOption" value="join"> 加入现有房间
					</label>
				</div>

				<div class="room-id-input" v-if="roomOption === 'join'">
					<input
						type="text"
						v-model="inputRoomId"
						placeholder="输入房间ID"
						:disabled="isConnected"
					>
				</div>

				<div class="created-room-id" v-if="roomOption === 'create' && createdRoomId">
					<p>房间ID: <strong>{{ createdRoomId }}</strong></p>
					<button @click="copyRoomId" class="btn-copy">
						<i class="fas fa-copy"></i> 复制房间ID
					</button>
				</div>
			</div>

			<!-- 视频容器：调整DOM结构，添加同级遮罩层 -->
			<div class="video-container">
				<!-- 主窗口：优先显示本地用户或当前选中的主讲人 -->
				<div v-if="mainParticipant"
					 class="video-wrapper main-video"
					 :class="{ 'focused': isFocused }"
				>
					<h3>{{ mainParticipant.isLocal ? '我的画面' : `参与者 ${mainParticipant.id.slice(-4)}` }}</h3>
					<video
						ref="mainVideo"
						autoplay
						muted="false"
						playsinline
						:muted="mainParticipant.isLocal"
					></video>
					<div class="video-controls" v-if="isConnected">
						<button @click="toggleFocus" class="btn-focus">
							<i class="fas" :class="isFocused ? 'fa-compress' : 'fa-expand'"></i>
							{{ isFocused ? '恢复默认' : '放大查看' }}
						</button>
					</div>
					<div class="media-fallback" v-if="!hasMedia && mainParticipant.isLocal">
						<i class="fas fa-video-slash"></i>
						<p>无法访问媒体设备</p>
						<p>请检查摄像头和麦克风权限</p>
					</div>
				</div>
				<div v-else class="no-participant">
					暂无参与者
				</div>
				<div class="overlay" @click="toggleFocus"></div>
				<!-- 小窗口：其他参与者 -->
				<div
					class="video-wrapper small-video"
					v-for="participant in remoteParticipants"
					:key="participant.id"
					@click="switchMainVideo(participant)"
				>
					<h3>参与者 {{ participant.id.slice(-4) }}</h3>
					<video
						:ref="'remoteVideo_' + participant.id"
						autoplay
						playsinline
					></video>
					<div class="participant-status" v-if="participant.isSpeaking">
						<i class="fas fa-microphone-alt"></i> 发言中
					</div>
				</div>
			</div>

			<!-- 控制栏：提升层级，确保始终可见 -->
			<div class="controls">
				<button @click="start" :disabled="isConnected" class="btn-start">
					<i class="fas fa-play"></i>{{ roomOption === 'create' ? '创建房间' : '加入房间' }}
				</button>
				<button @click="stop" :disabled="!isConnected" class="btn-stop">
					<i class="fas fa-stop"></i>离开房间
				</button>
				<button @click="toggleVideo" class="btn-toggle" :disabled="!isConnected">
					<i class="fas fa-video"></i>{{ videoEnabled ? '关闭视频' : '开启视频' }}
				</button>
				<button @click="toggleAudio" class="btn-toggle" :disabled="!isConnected">
					<i class="fas fa-microphone"></i>{{ audioEnabled ? '静音' : '取消静音' }}
				</button>
				<button @click="shareScreen" :disabled="!isConnected" class="btn-screen">
					<i class="fas fa-desktop"></i>{{ isScreenSharing ? '停止共享' : '共享屏幕' }}
				</button>
			</div>

			<div v-if="error" class="error">
				<i class="fas fa-exclamation-circle"></i> {{ error }}
			</div>

			<div class="status" :class="connectionStatus">
				<i :class="statusIcon"></i> {{ statusMessage }}
			</div>

			<div class="instructions">
				<h3><i class="fas fa-info-circle"></i> 使用说明</h3>
				<ol>
					<li>选择"创建新房间"或"加入现有房间"</li>
					<li>点击"创建房间"或"加入房间"按钮初始化WebRTC连接</li>
					<li>允许浏览器访问您的摄像头和麦克风</li>
					<li>连接成功后，点击"共享屏幕"按钮开始屏幕共享</li>
					<li>使用其他按钮控制视频、音频和连接状态</li>
					<li>点击小窗口可以切换主窗口显示</li>
					<li>视频放大后，可通过ESC键、点击遮罩层或"恢复默认"按钮退出放大</li>
				</ol>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		name: 'WebRTCScreenShare',
		data() {
			return {
				isConnected: false,
				isScreenSharing: false,
				videoEnabled: true,
				audioEnabled: true,
				error: null,
				statusMessage: '准备就绪',
				connectionStatus: '',
				localStream: null,
				screenStream: null,
				janus: null,          // Janus实例
				session: null,        // Janus会话
				pluginHandle: null,   // VideoRoom插件句柄
				sessionId: null,
				handleId: null,
				roomOption: 'create',
				inputRoomId: '',
				createdRoomId: null,
				currentRoomId: null,
				isCreator: false,
				isFocused: false,
				participants: [],     // 房间内所有参与者
				localParticipantId: null,
				hasMedia: false,
				janusInitialized: false, // 标记Janus库是否初始化完成
				adapterLoaded: false,  // 标记adapter.js是否加载完成

				// Janus配置（适配你的服务器和端口）
				janusConfig: {
					// 测试用WS（8188），生产改用WSS（8189）
					// server: 'ws://janus.vipss.vip:8188/janus',
					// 生产环境WSS配置（启用证书后切换）
					server: 'wss://janus.vipss.vip:8189/janus',
					fallback: "ws://janus.vipss.vip:8188/janus", // 降级备用地址
					adminSecret: 'janusoverlord',
					videoroomPlugin: 'janus.plugin.videoroom',
					// adapter.js加载地址
					adapterJsUrl: 'https://webrtc.github.io/adapter/adapter-latest.js',
					// janus.js的备选加载地址
					janusJsUrls: [
						'/static/js/janus.js', // 优先本地静态文件
						'https://cdn.jsdelivr.net/npm/janus-gateway@1.4.1/dist/janus.js', // CDN备用
						'https://janus.vipss.vip:8089/html/assets/js/janus.js' // Janus服务器原生路径
					]
				},

				// 浏览器兼容性检测
				isWebRTCSupported: false,
				navigatorCheck: false,
				mediaDevicesCheck: false,
				getUserMediaCheck: false,
				peerConnectionCheck: false,
				isSecureContext: false
			};
		},
		computed: {
			statusIcon() {
				if (this.connectionStatus === 'connected') return 'fas fa-check-circle';
				if (this.connectionStatus === 'connecting') return 'fas fa-sync-alt fa-spin';
				if (this.connectionStatus === 'disconnected') return 'fas fa-times-circle';
				return 'fas fa-info-circle';
			},
			remoteParticipants() {
				return this.participants.filter(p => !p.isLocal);
			},
			mainParticipant() {
				const localUser = this.participants.find(p => p.isLocal);
				const firstRemoteUser = this.remoteParticipants[0];
				return localUser || firstRemoteUser || null;
			}
		},
		methods: {
			updateStatus(message, status) {
				this.statusMessage = message;
				this.connectionStatus = status;
				console.log(`[Status] ${status}: ${message}`);
			},

			// 浏览器兼容性检测
			checkWebRTCSupport() {
				// 1. 检查navigator对象是否存在
				this.navigatorCheck = typeof navigator !== 'undefined';

				// 2. 检查是否在安全上下文中运行
				this.isSecureContext = window.isSecureContext;

				// 3. 检查mediaDevices是否存在
				this.mediaDevicesCheck = this.navigatorCheck &&
					typeof navigator.mediaDevices !== 'undefined';

				// 4. 检查getUserMedia方法是否存在
				this.getUserMediaCheck = this.mediaDevicesCheck &&
					typeof navigator.mediaDevices.getUserMedia === 'function';

				// 5. 检查RTCPeerConnection是否存在
				this.peerConnectionCheck = typeof RTCPeerConnection !== 'undefined' ||
					typeof webkitRTCPeerConnection !== 'undefined' ||
					typeof mozRTCPeerConnection !== 'undefined';

				// 综合判断WebRTC支持情况（放宽安全上下文限制，方便测试）
				this.isWebRTCSupported = this.navigatorCheck &&
					this.mediaDevicesCheck &&
					this.getUserMediaCheck &&
					this.peerConnectionCheck;

				if (!this.isWebRTCSupported) {
					console.error('WebRTC支持检测失败:');
					console.error(`- navigator对象: ${this.navigatorCheck}`);
					console.error(`- mediaDevices: ${this.mediaDevicesCheck}`);
					console.error(`- getUserMedia: ${this.getUserMediaCheck}`);
					console.error(`- RTCPeerConnection: ${this.peerConnectionCheck}`);
					console.error(`- 安全上下文: ${this.isSecureContext}`);
				}
			},

			// 加载adapter.js（WebRTC兼容层）
			async loadAdapterLibrary() {
				// 检查是否已加载
				if (window.adapter || this.adapterLoaded) {
					console.log("adapter.js已加载，直接使用");
					this.adapterLoaded = true;
					return Promise.resolve();
				}

				console.log(`开始加载adapter.js: ${this.janusConfig.adapterJsUrl}`);
				return new Promise((resolve, reject) => {
					// 移除已存在的脚本
					const existingScript = document.querySelector(`script[src="${this.janusConfig.adapterJsUrl}"]`);
					if (existingScript) {
						existingScript.remove();
					}

					const script = document.createElement('script');
					script.src = this.janusConfig.adapterJsUrl;
					script.type = 'text/javascript';
					script.async = false; // 同步加载，确保先于janus.js加载
					script.crossOrigin = 'anonymous';

					// 成功回调
					script.onload = () => {
						if (window.adapter) {
							console.log("adapter.js加载成功");
							this.adapterLoaded = true;
							resolve();
						} else {
							reject(new Error("adapter.js加载成功，但未找到window.adapter对象"));
						}
					};

					// 错误回调
					script.onerror = (event) => {
						const errorMsg = event.message || `无法加载adapter.js: ${this.janusConfig.adapterJsUrl}`;
						reject(new Error(errorMsg));
					};

					// 超时回调
					setTimeout(() => {
						reject(new Error(`adapter.js加载超时（超过10秒）`));
					}, 10000);

					document.head.appendChild(script);
				});
			},

			// 重构Janus库加载逻辑：先加载adapter，再加载janus
			async loadJanusLibrary() {
				// 1. 先加载adapter.js（依赖前置）
				await this.loadAdapterLibrary();

				// 2. 检查是否已加载并初始化
				if (window.Janus && this.janusInitialized) {
					console.log("Janus库已存在且初始化完成，直接使用");
					return Promise.resolve(window.Janus);
				}

				// 3. 依次尝试多个加载地址，直到成功
				if (!window.Janus) {
					for (const url of this.janusConfig.janusJsUrls) {
						try {
							console.log(`尝试加载Janus库: ${url}`);
							await this.loadScript(url);
							if (window.Janus) {
								console.log(`Janus库加载成功: ${url}`);
								break;
							}
						} catch (err) {
							console.warn(`加载${url}失败:`, err.message);
							// 如果是最后一个地址仍失败，抛出错误
							if (url === this.janusConfig.janusJsUrls[this.janusConfig.janusJsUrls.length - 1]) {
								throw new Error(`加载Janus库失败: ${err.message}`);
							}
							continue; // 失败则尝试下一个地址
						}
					}
				}

				// 4. 初始化Janus库
				return new Promise((resolve, reject) => {
					if (this.janusInitialized) {
						resolve(window.Janus);
						return;
					}

					console.log("开始初始化Janus库...");
					window.Janus.init({
						debug: 'error', // 生产环境用error，调试用all
						callback: (err) => {
							if (err) {
								reject(new Error(`Janus库初始化失败: ${err}`));
								return;
							}
							this.janusInitialized = true;
							console.log("Janus库初始化成功");
							resolve(window.Janus);
						}
					});
				});
			},

			// 辅助方法：加载单个脚本
			loadScript(url) {
				return new Promise((resolve, reject) => {
					// 先移除已存在的同名脚本
					const existingScript = document.querySelector(`script[src="${url}"]`);
					if (existingScript) {
						existingScript.remove();
					}

					const script = document.createElement('script');
					script.src = url;
					script.type = 'text/javascript';
					script.async = true;
					script.crossOrigin = 'anonymous'; // 解决跨域问题

					// 成功回调
					script.onload = () => {
						if (window.Janus) {
							resolve();
						} else {
							reject(new Error(`脚本加载成功，但未找到window.Janus对象`));
						}
					};

					// 错误回调
					script.onerror = (event) => {
						const errorMsg = event.message || `无法加载脚本: ${url}`;
						const errorCode = event.target?.status || '未知错误';
						reject(new Error(`${errorMsg} (错误码: ${errorCode})`));
					};

					// 超时回调
					setTimeout(() => {
						reject(new Error(`加载超时: ${url} (超过10秒)`));
					}, 10000);

					document.head.appendChild(script);
				});
			},

			// 初始化Janus连接
			initJanusConnection() {
				return new Promise((resolve, reject) => {
					// 前置检查
					if (!window.Janus || !this.janusInitialized) {
						reject(new Error("Janus库未完成初始化，请先调用loadJanusLibrary"));
						return;
					}

					console.log("开始连接Janus服务器:", this.janusConfig.server);
					// 初始化Janus
					this.janus = new window.Janus({
						server: this.janusConfig.server,
						fallback: this.janusConfig.fallback,
						iceServers: [
							{ urls: 'stun:stun.l.google.com:19302' },
							{ urls: 'stun:stun1.l.google.com:19302' }
						],
						// 测试环境忽略证书错误（WSS时需要）
						ignore_server_certificate: true,
						success: () => {
							console.log("Janus连接成功");
							// 创建Janus会话
							this.janus.createSession({
								success: (session) => {
									this.session = session;
									console.log("Janus会话创建成功，ID:", session.id);
									// 附加VideoRoom插件
									session.attach({
										plugin: this.janusConfig.videoroomPlugin,
										success: (pluginHandle) => {
											this.pluginHandle = pluginHandle;
											console.log("VideoRoom插件附加成功");
											// 注册消息处理
											pluginHandle.onmessage = (msg, jsep) => {
												this.handleJanusMessage(msg, jsep);
											};
											pluginHandle.onremotestream = (stream, mid, index) => {
												this.handleRemoteStream(stream, mid, index);
											};
											pluginHandle.onlocalstream = (stream) => {
												console.log("本地流已发布", stream);
											};
											resolve(); // 初始化完成
										},
										error: (err) => {
											reject(new Error('附加VideoRoom插件失败: ' + err));
										}
									});
								},
								error: (err) => {
									reject(new Error('创建Janus会话失败: ' + err));
								}
							});
						},
						error: (err) => {
							reject(new Error('连接Janus服务器失败: ' + err));
						},
						destroyed: () => {
							console.log("Janus连接已销毁");
							this.isConnected = false;
						}
					});
				});
			},

			async start() {
				try {
					// 确定房间ID
					if (this.roomOption === 'create') {
						this.createdRoomId = Math.floor(100000 + Math.random() * 900000).toString();
						this.currentRoomId = this.createdRoomId;
						this.isCreator = true;
						this.updateStatus("正在创建房间...", "connecting");
					} else {
						if (!this.inputRoomId.trim()) {
							this.error = '请输入有效的房间ID';
							return;
						}
						this.currentRoomId = this.inputRoomId.trim();
						this.isCreator = false;
						this.updateStatus("正在加入房间...", "connecting");
					}

					this.error = null;

					// 1. 加载并初始化Janus库
					this.updateStatus("正在加载WebRTC依赖库...", "connecting");
					await this.loadJanusLibrary();

					// 2. 获取用户媒体设备
					try {
						this.updateStatus("正在获取摄像头/麦克风权限...", "connecting");
						this.localStream = await this.getUserMedia();
						this.hasMedia = true;

						// 显示本地视频
						if (this.localStream && this.$refs.mainVideo) {
							this.$refs.mainVideo.srcObject = this.localStream;
						}

					} catch (err) {
						this.error = '无法访问媒体设备: ' + err.message;
						console.error(err);
						this.updateStatus("媒体设备访问失败", "disconnected");
						this.hasMedia = false;
						return;
					}

					// 3. 初始化Janus连接
					this.updateStatus("正在连接Janus服务器...", "connecting");
					await this.initJanusConnection();

					// 4. 创建/加入VideoRoom
					if (this.isCreator) {
						await this.createJanusVideoRoom();
					} else {
						await this.joinJanusVideoRoom();
					}

					this.updateStatus("已连接至房间 " + this.currentRoomId, "connected");
					this.isConnected = true;

				} catch (err) {
					this.error = '启动WebRTC失败: ' + err.message;
					console.error(err);
					this.updateStatus("连接失败", "disconnected");
				}
			},

			// 创建Janus VideoRoom
			createJanusVideoRoom() {
				return new Promise((resolve, reject) => {
					if (!this.pluginHandle) {
						reject(new Error("插件句柄未初始化"));
						return;
					}

					const createRoomRequest = {
						request: "create",
						room: parseInt(this.currentRoomId),
						description: "视频辅导房间",
						secret: "roomsecret",       // 房间密钥
						pin: "1234",               // 房间密码（可选）
						is_private: false,
						bitrate: 1024,
						fir_freq: 10
					};

					this.pluginHandle.send({
						message: createRoomRequest,
						success: (response) => {
							console.log("房间创建成功", response);
							// 创建房间后加入房间
							this.joinJanusVideoRoom().then(resolve).catch(reject);
						},
						error: (err) => {
							reject(new Error('创建房间失败: ' + err));
						}
					});
				});
			},

			// 加入Janus VideoRoom
			joinJanusVideoRoom() {
				return new Promise((resolve, reject) => {
					if (!this.pluginHandle) {
						reject(new Error("插件句柄未初始化"));
						return;
					}

					// 生成唯一的参与者ID
					this.localParticipantId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);

					const joinRoomRequest = {
						request: "join",
						room: parseInt(this.currentRoomId),
						ptype: "publisher",
						display: this.localParticipantId,
						secret: "roomsecret"  // 匹配创建房间时的secret
					};

					this.pluginHandle.send({
						message: joinRoomRequest,
						jsep: null,
						success: (response) => {
							console.log("加入房间成功", response);

							// 添加本地参与者
							this.participants.push({
								id: this.localParticipantId,
								isLocal: true,
								isSpeaking: false,
								stream: this.localStream
							});

							// 发布本地流
							this.publishLocalStream().then(resolve).catch(reject);
						},
						error: (err) => {
							reject(new Error('加入房间失败: ' + err));
						}
					});
				});
			},

			// 发布本地流到房间
			publishLocalStream() {
				return new Promise((resolve, reject) => {
					if (!this.pluginHandle || !this.localStream) {
						reject(new Error("插件句柄或本地流未初始化"));
						return;
					}

					// 创建发布请求
					const publishRequest = {
						request: "configure",
						bitrate: 1024,
						audio: true,
						video: true,
						data: false
					};

					// 创建JSEP（WebRTC会话描述）
					this.pluginHandle.createOffer({
						media: {
							audioRecv: false,
							videoRecv: false,
							audioSend: true,
							videoSend: true
						},
						stream: this.localStream,
						success: (jsep) => {
							console.log("创建Offer成功", jsep);
							// 发送发布请求和JSEP
							this.pluginHandle.send({
								message: publishRequest,
								jsep: jsep,
								success: (response) => {
									console.log("本地流发布成功", response);
									resolve();
								},
								error: (err) => {
									reject(new Error('发布本地流失败: ' + err));
								}
							});
						},
						error: (err) => {
							reject(new Error('创建Offer失败: ' + err));
						}
					});
				});
			},

			// 处理Janus消息
			handleJanusMessage(msg, jsep) {
				console.log("收到Janus消息", msg, jsep);

				const event = msg.videoroom;
				if (!event) return;

				// 处理房间事件
				switch (event) {
					case "joined":
						console.log("成功加入房间", msg);
						break;

					case "event":
						// 处理参与者列表更新
						if (msg.participants && msg.participants.length > 0) {
							this.updateParticipantsList(msg.participants);
						}
						break;

					case "publish":
						console.log("本地流发布成功", msg);
						break;

					case "slowlink":
						console.warn("网络连接缓慢", msg);
						break;

					case "error":
						this.error = 'Janus错误: ' + msg.error;
						console.error("Janus错误", msg);
						break;
				}

				// 处理JSEP（应答）
				if (jsep) {
					this.pluginHandle.handleRemoteJsep({ jsep: jsep });
				}
			},

			// 处理远程流
			handleRemoteStream(stream, mid, index) {
				console.log("收到远程流", stream, mid, index);

				// 生成远程参与者ID
				const remoteParticipantId = 'remote_' + Date.now() + '_' + index;

				// 添加远程参与者
				this.participants.push({
					id: remoteParticipantId,
					isLocal: false,
					isSpeaking: false,
					stream: stream
				});

				// 渲染远程视频
				this.$nextTick(() => {
					const videoRef = this.$refs[`remoteVideo_${remoteParticipantId}`];
					if (videoRef && stream) {
						videoRef.srcObject = stream;
						console.log("远程视频已渲染", remoteParticipantId);
					}
				});
			},

			// 更新参与者列表
			updateParticipantsList(participants) {
				console.log("更新参与者列表", participants);

				// 订阅所有远程参与者的流
				participants.forEach((participant) => {
					// 跳过本地参与者
					if (participant.id === this.pluginHandle.getId()) return;

					const subscribeRequest = {
						request: "subscribe",
						room: parseInt(this.currentRoomId),
						feed: participant.id
					};

					// 订阅远程流
					this.pluginHandle.send({
						message: subscribeRequest,
						success: (response) => {
							console.log("订阅远程流成功", participant.id);
						},
						error: (err) => {
							console.error("订阅远程流失败", participant.id, err);
						}
					});
				});
			},

			// 获取真实的用户媒体
			async getUserMedia() {
				try {
					return await navigator.mediaDevices.getUserMedia({
						video: {
							width: { ideal: 1280 },
							height: { ideal: 720 },
							frameRate: { ideal: 30 }
						},
						audio: {
							echoCancellation: true,
							noiseSuppression: true,
							autoGainControl: true
						}
					});
				} catch (err) {
					console.error('无法获取真实媒体设备:', err.message);
					throw err;
				}
			},

			stop() {
				// 离开房间
				if (this.pluginHandle) {
					const leaveRequest = {
						request: "leave",
						room: parseInt(this.currentRoomId) || 0
					};

					this.pluginHandle.send({
						message: leaveRequest,
						success: () => console.log("成功离开房间"),
						error: (err) => console.error("离开房间失败", err)
					});

					// 销毁插件句柄
					this.pluginHandle.detach();
				}

				// 销毁Janus会话
				if (this.session) {
					this.session.destroy();
				}

				// 销毁Janus实例
				if (this.janus) {
					this.janus.destroy();
					this.janusInitialized = false;
					this.adapterLoaded = false;
				}

				// 停止媒体流
				if (this.localStream && typeof this.localStream.getTracks === 'function') {
					this.localStream.getTracks().forEach(track => track.stop());
				}

				if (this.screenStream && typeof this.screenStream.getTracks === 'function') {
					this.screenStream.getTracks().forEach(track => track.stop());
				}

				// 重置状态
				this.localStream = null;
				this.screenStream = null;
				this.janus = null;
				this.session = null;
				this.pluginHandle = null;
				this.isConnected = false;
				this.isScreenSharing = false;
				this.currentRoomId = null;
				this.isCreator = false;
				this.isFocused = false;
				this.hasMedia = false;
				this.participants = [];

				if (this.$refs.mainVideo) {
					this.$refs.mainVideo.srcObject = null;
				}

				this.updateStatus("已断开连接", "disconnected");
			},

			toggleVideo() {
				if (this.localStream && typeof this.localStream.getVideoTracks === 'function') {
					const videoTrack = this.localStream.getVideoTracks()[0];
					if (videoTrack) {
						videoTrack.enabled = !videoTrack.enabled;
						this.videoEnabled = videoTrack.enabled;

						// 更新Janus发布配置
						if (this.pluginHandle) {
							this.pluginHandle.send({
								message: {
									request: "configure",
									video: this.videoEnabled
								}
							});
						}
					}
				}
			},

			toggleAudio() {
				if (this.localStream && typeof this.localStream.getAudioTracks === 'function') {
					const audioTrack = this.localStream.getAudioTracks()[0];
					if (audioTrack) {
						audioTrack.enabled = !audioTrack.enabled;
						this.audioEnabled = audioTrack.enabled;

						// 更新Janus发布配置
						if (this.pluginHandle) {
							this.pluginHandle.send({
								message: {
									request: "configure",
									audio: this.audioEnabled
								}
							});
						}
					}
				}
			},

			async shareScreen() {
				if (this.isScreenSharing) {
					this.switchToCamera();
					return;
				}

				try {
					this.updateStatus("正在获取屏幕共享权限...", "connecting");

					// 获取屏幕共享流
					this.screenStream = await this.getDisplayMedia();

					// 设置屏幕共享结束事件
					if (this.screenStream && typeof this.screenStream.getVideoTracks === 'function') {
						const videoTrack = this.screenStream.getVideoTracks()[0];
						if (videoTrack) {
							videoTrack.onended = () => {
								this.switchToCamera();
							};
						}
					}

					// 替换本地视频源
					if (this.screenStream && this.$refs.mainVideo) {
						this.$refs.mainVideo.srcObject = this.screenStream;
					}

					// 更新Janus发布的流
					if (this.pluginHandle) {
						// 停止旧的视频轨道
						if (this.localStream) {
							const oldVideoTrack = this.localStream.getVideoTracks()[0];
							if (oldVideoTrack) {
								oldVideoTrack.stop();
							}
						}

						// 替换本地流的视频轨道
						const screenVideoTrack = this.screenStream.getVideoTracks()[0];
						this.localStream.removeTrack(this.localStream.getVideoTracks()[0]);
						this.localStream.addTrack(screenVideoTrack);

						// 重新发布流
						this.publishLocalStream();
					}

					this.isScreenSharing = true;
					this.updateStatus("屏幕共享已启动", "connected");

				} catch (err) {
					this.error = '屏幕共享失败: ' + err.message;
					console.error(err);
					this.updateStatus("屏幕共享失败", "disconnected");
				}
			},

			// 获取真实的屏幕共享媒体
			async getDisplayMedia() {
				try {
					return await navigator.mediaDevices.getDisplayMedia({
						video: {
							cursor: "always"
						},
						audio: false
					});
				} catch (err) {
					console.error('无法获取屏幕共享:', err.message);
					throw err;
				}
			},

			switchToCamera() {
				if (this.localStream && this.$refs.mainVideo) {
					// 恢复摄像头流
					this.getUserMedia().then((cameraStream) => {
						this.localStream = cameraStream;
						this.$refs.mainVideo.srcObject = this.localStream;

						// 停止屏幕共享流
						if (this.screenStream && typeof this.screenStream.getTracks === 'function') {
							this.screenStream.getTracks().forEach(track => track.stop());
							this.screenStream = null;
						}

						// 重新发布摄像头流
						if (this.pluginHandle) {
							this.publishLocalStream();
						}

						this.isScreenSharing = false;
						this.updateStatus("已切换回摄像头", "connected");
					}).catch(err => {
						this.error = '恢复摄像头失败: ' + err.message;
						console.error(err);
					});
				}
			},

			copyRoomId() {
				if (this.createdRoomId) {
					navigator.clipboard.writeText(this.createdRoomId)
						.then(() => {
							alert('房间ID已复制到剪贴板');
						})
						.catch(err => {
							console.error('无法复制房间ID: ', err);
						});
				}
			},

			switchMainVideo(participant) {
				console.log('切换主视频到参与者:', participant.id);

				// 渲染选中的远程流到主窗口
				if (this.$refs.mainVideo && participant.stream) {
					this.$refs.mainVideo.srcObject = participant.stream;
				}
			},

			toggleFocus() {
				this.isFocused = !this.isFocused;
				// 放大时添加提示，缩小后移除提示
				if (this.isFocused) {
					this.error = "视频已放大，按ESC键或点击遮罩层可恢复";
				} else {
					this.error = null;
				}
			},

			// 键盘事件监听
			handleKeydown(e) {
				// ESC键退出放大
				if (e.key === 'Escape' && this.isFocused) {
					this.toggleFocus();
				}
			}
		},
		mounted() {
			this.checkWebRTCSupport();
			this.updateStatus("准备就绪", "");
			// 监听键盘事件
			window.addEventListener('keydown', this.handleKeydown);
		},
		beforeUnmount() {
			// 销毁时移除监听，避免内存泄漏
			window.removeEventListener('keydown', this.handleKeydown);
			// 断开连接
			if (this.isConnected) {
				this.stop();
			}
		}
	};
</script>
<style scoped>
	/* 保持原有样式不变 */
	.webrtc-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		background: white;
		border-radius: 15px;
		box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
		overflow: hidden;
		width: 100%;
		max-width: 900px;
		padding: 25px;
		margin: 20px auto;
	}

	.header {
		text-align: center;
		margin-bottom: 20px;
		width: 100%;
	}

	.header h1 {
		color: #2c3e50;
		margin-bottom: 10px;
		font-weight: 600;
	}

	.header p {
		color: #666;
		font-size: 16px;
	}

	/* 浏览器兼容性检查样式 */
	.browser-check {
		width: 100%;
		padding: 20px;
		background: #fff8e1;
		border-radius: 10px;
		border-left: 4px solid #ffc107;
	}

	.browser-header {
		display: flex;
		align-items: center;
		gap: 15px;
		margin-bottom: 20px;
	}

	.browser-header i {
		font-size: 36px;
		color: #ffc107;
	}

	.browser-header h2 {
		color: #333;
		font-weight: 600;
	}

	.browser-status {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-bottom: 25px;
	}

	.status-item {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 12px;
		border-radius: 8px;
		font-size: 16px;
		font-weight: 500;
	}

	.status-pass {
		background: #e8f5e9;
		color: #2e7d32;
	}

	.status-fail {
		background: #ffebee;
		color: #c62828;
	}

	.status-item i {
		font-size: 20px;
	}

	.browser-recommendation {
		margin-bottom: 25px;
	}

	.browser-recommendation h3 {
		margin-bottom: 15px;
		color: #333;
		font-weight: 600;
	}

	.browser-list {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 15px;
	}

	.browser-item {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 15px;
		background: white;
		border-radius: 8px;
		box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
		transition: transform 0.3s;
	}

	.browser-item:hover {
		transform: translateY(-5px);
	}

	.browser-item i {
		font-size: 36px;
		margin-bottom: 10px;
	}

	.browser-item span:nth-child(2) {
		font-weight: 600;
		margin-bottom: 5px;
	}

	.browser-item span:nth-child(3) {
		font-size: 14px;
		color: #666;
	}

	.fa-chrome { color: #4285F4; }
	.fa-firefox { color: #FF7139; }
	.fa-edge { color: #0078D7; }
	.fa-safari { color: #0066CC; }

	.browser-troubleshooting {
		background: #e3f2fd;
		padding: 20px;
		border-radius: 8px;
	}

	.browser-troubleshooting h3 {
		margin-bottom: 15px;
		color: #333;
		font-weight: 600;
	}

	.browser-troubleshooting ul {
		list-style-type: none;
	}

	.browser-troubleshooting li {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 10px 0;
		border-bottom: 1px solid #bbdefb;
	}

	.browser-troubleshooting li:last-child {
		border-bottom: none;
	}

	.browser-troubleshooting i {
		color: #2196F3;
		font-size: 18px;
	}

	/* 房间管理样式 */
	.room-management {
		width: 100%;
		margin-bottom: 20px;
		padding: 15px;
		background: #f9f9f9;
		border-radius: 8px;
	}

	.room-options {
		display: flex;
		gap: 20px;
		margin-bottom: 15px;
	}

	.room-options label {
		display: flex;
		align-items: center;
		gap: 5px;
		cursor: pointer;
	}

	.room-id-input {
		margin-bottom: 15px;
	}

	.room-id-input input {
		padding: 10px;
		border: 1px solid #ddd;
		border-radius: 4px;
		width: 100%;
		max-width: 300px;
	}

	.created-room-id {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-bottom: 15px;
	}

	.btn-copy {
		padding: 6px 12px;
		background: #3498db;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 14px;
	}

	/* 视频容器样式 */
	.video-container {
		display: flex;
		flex-wrap: wrap;
		gap: 20px;
		margin-bottom: 25px;
		width: 100%;
		justify-content: center;
		transition: all 0.3s ease;
	}

	.video-wrapper {
		position: relative;
		border-radius: 10px;
		overflow: hidden;
		box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
		background: #000;
		width: 100%;
		max-width: 400px;
		transition: all 0.3s ease;
	}

	.main-video {
		flex: 2;
	}

	.small-video {
		flex: 1;
		cursor: pointer;
	}

	.small-video:hover {
		transform: translateY(-5px);
		box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
	}

	/* 视频放大样式优化 */
	.main-video.focused {
		position: fixed;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 90vw;
		height: 90vh;
		z-index: 1000;
		max-width: none;
		box-shadow: 0 0 0 100vw rgba(0,0,0,0.8);
	}

	.main-video.focused video {
		height: 100%;
		object-fit: contain;
	}

	/* 遮罩层样式 */
	.overlay {
		display: none;
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.8);
		z-index: 999;
	}

	/* 确保放大时显示遮罩层 */
	.main-video.focused + .overlay {
		display: block;
	}

	.video-wrapper h3 {
		position: absolute;
		top: 10px;
		left: 10px;
		background: rgba(0, 0, 0, 0.6);
		color: white;
		padding: 5px 10px;
		border-radius: 4px;
		font-size: 14px;
		z-index: 10;
	}

	video {
		width: 100%;
		height: 250px;
		background-color: #000;
		display: block;
		object-fit: cover;
	}

	.no-participant {
		grid-column: 1 / -1;
		height: 250px;
		width: 400px;
		display: flex;
		justify-content: center;
		align-items: center;
		background: #ecf0f1;
		border-radius: 10px;
		color: #7f8c8d;
		font-size: 18px;
		font-weight: 500;
	}

	.participant-status {
		position: absolute;
		bottom: 10px;
		left: 10px;
		background: rgba(46, 204, 113, 0.8);
		color: white;
		padding: 4px 8px;
		border-radius: 4px;
		font-size: 12px;
		z-index: 10;
		display: flex;
		align-items: center;
		gap: 5px;
	}

	/* 控制栏样式优化 */
	.controls {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		margin-bottom: 20px;
		justify-content: center;
		width: 100%;
		position: relative;
		z-index: 2000; /* 确保操作栏始终在最上层 */
	}

	button {
		padding: 12px 18px;
		color: white;
		border: none;
		border-radius: 6px;
		cursor: pointer;
		font-weight: 600;
		transition: all 0.3s;
		display: flex;
		align-items: center;
		gap: 8px;
	}

	button:hover:not(:disabled) {
		transform: translateY(-2px);
		box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
	}

	button:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.btn-start {
		background: linear-gradient(90deg, #4CAF50 0%, #2E7D32 100%);
	}

	.btn-stop {
		background: linear-gradient(90deg, #f44336 0%, #d32f2f 100%);
	}

	.btn-toggle {
		background: linear-gradient(90deg, #2196F3 0%, #1976D2 100%);
	}

	.btn-screen {
		background: linear-gradient(90deg, #FF9800 0%, #F57C00 100%);
	}

	.error {
		color: #c62828;
		margin-top: 10px;
		text-align: center;
		padding: 12px;
		background: #ffebee;
		border-radius: 6px;
		width: 100%;
		font-weight: 500;
		position: relative;
		z-index: 2000;
	}

	.status {
		margin-top: 15px;
		padding: 12px;
		border-radius: 6px;
		text-align: center;
		width: 100%;
		font-weight: 500;
		position: relative;
		z-index: 2000;
	}

	.status.connected {
		background-color: #e8f5e9;
		color: #2e7d32;
	}

	.status.connecting {
		background-color: #fff8e1;
		color: #f57c00;
	}

	.status.disconnected {
		background-color: #ffebee;
		color: #c62828;
	}

	.instructions {
		background: #f5f5f5;
		padding: 15px;
		border-radius: 8px;
		margin-top: 20px;
		width: 100%;
		position: relative;
		z-index: 2000;
	}

	.instructions h3 {
		margin-bottom: 10px;
		color: #333;
	}

	.instructions ol {
		padding-left: 20px;
		color: #555;
	}

	.instructions li {
		margin-bottom: 8px;
		line-height: 1.5;
	}

	.media-fallback {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100%;
		color: white;
		text-align: center;
		padding: 20px;
		background: #34495e;
	}

	.media-fallback i {
		font-size: 48px;
		margin-bottom: 15px;
		color: #7f8c8d;
	}

	.video-controls {
		position: absolute;
		bottom: 30px;
		right: 30px;
		z-index: 1001;
	}

	.btn-focus {
		padding: 12px 20px;
		background: rgba(231, 76, 60, 0.9);
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 16px;
	}

	@media (max-width: 768px) {
		.video-container {
			flex-direction: column;
		}

		.video-wrapper {
			width: 100%;
		}

		video {
			height: 200px;
		}

		.controls {
			flex-direction: column;
			align-items: center;
		}

		button {
			width: 100%;
			justify-content: center;
		}

		.room-options {
			flex-direction: column;
			gap: 10px;
		}

		.main-video.focused {
			width: 95vw;
			height: 95vh;
		}
	}
</style>
